import { useState, type ReactNode } from 'react';
import logoUrl from '@/assets/logo.jpg';
import type { SessionUser } from '@/auth/session';
import {
  ADMIN_NAV_GROUPS,
  CUSTOMER_BOTTOM_NAV,
  customerSideNavGroups,
  type AdminScreenKey,
  type CustomerScreenKey,
} from '@/nav/keys';
import { Icon } from '@/components/icons';
import { Money } from '@/components/ui';

export function AppShell({
  mode,
  user,
  active,
  onNavigate,
  onLogout,
  children,
  customerWallet = 'units',
}: {
  mode: 'customer' | 'admin';
  user: SessionUser;
  active: string;
  onNavigate: (key: string) => void;
  onLogout: () => void;
  children: ReactNode;
  customerWallet?: 'units' | 'games' | 'none';
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isAdmin = mode === 'admin';
  const apiEnabled = Number(user.api_enabled ?? 0) === 1;

  const customerGroups = customerSideNavGroups({
    role: user.role,
    apiEnabled,
    quickPayLabel: user.role === 'distributor' ? 'إرسال رصيد سريع' : 'الدفع السريع QR',
  });

  const sideGroups = isAdmin ? ADMIN_NAV_GROUPS : customerGroups;

  return (
    <main
      className={`app-shell ${isAdmin ? 'admin-shell vi-admin-layout' : 'customer-shell customer-v12-shell'}`}
      lang="ar"
      dir="rtl"
    >
      {isAdmin ? (
        <header className="topbar topbar-simple">
          <button type="button" className="menu-btn" onClick={() => setMenuOpen(true)} aria-label="القائمة">
            <Icon name="more" size={22} />
          </button>
          <div className="admin-top-title">
            <strong>لوحة الإدارة</strong>
            <small>لوحة إدارة متكاملة</small>
          </div>
          <div className="topbar-balance dual-balance">
            <div>
              <small>محفظة الوحدات</small>
              <strong>
                <Money value={user.wallet_balance} />
              </strong>
            </div>
            <div>
              <small>محفظة الألعاب</small>
              <strong>
                <Money value={user.game_wallet_balance ?? user.games_wallet_balance ?? 0} suffix="$" />
              </strong>
            </div>
          </div>
        </header>
      ) : (
        <header className="customer-topbar">
          <div className="customer-profile-row">
            <div className="customer-profile-chip">
              <span className="customer-avatar">
                <img src={logoUrl} alt="اشحنلي" />
              </span>
              <div>
                <small>أهلًا بك</small>
                <strong>{user.name}</strong>
              </div>
            </div>
            <button
              type="button"
              className="customer-menu-btn"
              onClick={() => setMenuOpen(true)}
              aria-label="القائمة"
            >
              <Icon name="more" size={22} />
            </button>
          </div>
          {customerWallet !== 'none' ? (
            <div className="customer-wallet-strip single-wallet">
              {customerWallet === 'units' ? (
                <article className="customer-wallet-card units">
                  <span>محفظة الوحدات</span>
                  <strong>
                    <Money value={user.wallet_balance || 0} />
                  </strong>
                </article>
              ) : (
                <article className="customer-wallet-card games">
                  <span>محفظة الألعاب</span>
                  <strong>
                    <Money
                      value={user.game_wallet_balance ?? user.games_wallet_balance ?? 0}
                      suffix="$"
                      fractionDigits={2}
                    />
                  </strong>
                </article>
              )}
            </div>
          ) : null}
        </header>
      )}

      <div
        className={`side-backdrop ${menuOpen ? 'show' : ''}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden
      />
      <aside className={`side-nav ${menuOpen ? 'open' : ''}`}>
        <div className="nav-brand">
          <img src={logoUrl} alt="" />
          <div>
            <strong>{isAdmin ? 'إدارة اشحنلي' : 'اشحنلي وحدات'}</strong>
            <span>{user.name}</span>
          </div>
          <button type="button" onClick={() => setMenuOpen(false)} aria-label="إغلاق">
            <Icon name="close" size={18} />
          </button>
        </div>

        {sideGroups.map((group) => (
          <nav key={group.group} className="nav-group">
            <p>{group.group}</p>
            {group.items.map((item) => (
              <button
                key={item.key}
                type="button"
                className={active === item.key ? 'active' : ''}
                onClick={() => {
                  onNavigate(item.key);
                  setMenuOpen(false);
                }}
              >
                <Icon name={item.icon} size={18} />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        ))}

        <button type="button" className="logout-btn" onClick={onLogout}>
          <Icon name="logout" size={18} />
          تسجيل الخروج
        </button>
      </aside>

      <section className="page-content">{children}</section>

      {!isAdmin ? (
        <nav className="customer-bottom-nav" aria-label="التنقل السريع">
          {CUSTOMER_BOTTOM_NAV.map((item) => {
            const activeClass =
              active === item.key || (item.key === 'home' && active === 'distributor') ? 'active' : '';
            return (
              <button
                key={item.key}
                type="button"
                className={activeClass}
                onClick={() => onNavigate(item.key)}
              >
                <Icon name={item.icon} size={20} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
      ) : null}
    </main>
  );
}

/** @deprecated */
export function TopBar(_props: {
  title: string;
  user?: SessionUser | null;
  onBack?: () => void;
  onLogout?: () => void;
}) {
  return null;
}
/** @deprecated */
export function BottomNav(_props: {
  active: CustomerScreenKey;
  onNavigate: (key: CustomerScreenKey) => void;
}) {
  return null;
}
/** @deprecated */
export function AdminSideNav(_props: {
  active: AdminScreenKey;
  onNavigate: (key: AdminScreenKey) => void;
}) {
  return null;
}
